import React, { useState } from 'react';
import { NavLink, useLocation, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '../utils/AuthContext';
import {
    LayoutDashboard,
    User,
    Users,
    Package,
    FileText,
    Receipt,
    Menu,
    X,
    Calculator,
    ChevronLeft,
    ChevronRight
} from 'lucide-react';
import UserMenu from './UserMenu';

const Layout = ({ children }) => {
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [isCollapsed, setIsCollapsed] = useState(false);
    const [openDropdown, setOpenDropdown] = useState(null);
    const location = useLocation();

    const navigation = [
        {
            name: 'Dashboard',
            href: '/dashboard',
            icon: LayoutDashboard,
            description: 'Overview with stats (invoices generated, customers, earnings)'
        },
        {
            name: 'Rate Master',
            href: '/rates',
            icon: Calculator,
            description: 'Manage freight rates and zones'
        },
        // {
        //   name: 'Profile',
        //   href: '/profile',
        //   icon: User,
        //   description: 'Your own franchise/company details including logo & bank info'
        // },
        {
            name: 'Customer Management',
            href: '/customers',
            icon: Users,
            description: 'Add/Edit/Delete customers (ID, name, GST, address, etc.)'
        },
        {
            name: 'Booking',
            href: '/booking',
            icon: Package,
            description: 'Add/modify bookings',
            children: [
                { name: 'Add', href: '/booking' },
                { name: 'Multiple Booking', href: '/booking/multiple' },
                { name: 'View Bookings', href: '/booking/multiple/view' },
                { name: 'Modify', href: '/booking/modify' },
                { name: 'Bulk Modify', href: '/booking/bulk-modify' }
            ]
        },
        {
            name: 'Invoice',
            href: '/invoice',
            icon: FileText,
            description: 'Generate and manage invoices',
            children: [
                { name: 'Generate GST Invoice', href: '/invoice-generator' },
                { name: 'Generate Non-GST Invoice', href: '/invoice-generator-non-gst' },
                { name: 'View Invoices', href: '/invoices' },
                // { name: 'Download Invoice', href: '/invoices/download' }
            ]
        },
    ];

    const navigate = useNavigate();
    const { user, signOut } = useAuth();

    const handleLogout = async () => {
        try {
            await signOut();
            navigate('/login', { replace: true });
        } catch (e) {
            // noop
        }
    };

    return (
        <div className="flex flex-col h-screen bg-gray-100">
            {/* Top Header Bar - Full Width, Fixed */}
            <div className="bg-white shadow-sm border-b border-gray-200 h-16 flex-none z-30 relative">
                <div className="flex items-center justify-between h-full px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center">
                        <button
                            onClick={() => setSidebarOpen(true)}
                            className="lg:hidden p-2 rounded-md text-gray-400 hover:text-gray-600 mr-2"
                        >
                            <Menu size={20} />
                        </button>
                        <img src="/assets/dashboard-logo.png" alt="FR-BillNet" className="h-12 w-auto object-contain" />
                    </div>
                    <div className="flex items-center space-x-4">
                        <UserMenu user={user} onLogout={handleLogout} />
                    </div>
                </div>
            </div>

            {/* Main Layout Body (Sidebar + Content) */}
            <div className="flex flex-1 overflow-hidden relative">

                {/* Mobile sidebar overlay */}
                {sidebarOpen && (
                    <div
                        className="fixed inset-0 z-40 bg-gray-600 bg-opacity-75 lg:hidden"
                        onClick={() => setSidebarOpen(false)}
                    />
                )}

                {/* Sidebar */}
                <div className={`
          absolute inset-y-0 left-0 z-50 bg-white shadow-lg transform transition-all duration-300 ease-in-out lg:static lg:translate-x-0
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
          ${isCollapsed ? 'w-20' : 'w-64'}
          border-r border-gray-200
        `}>
                    {/* Sidebar Header (Collapse Toggle only) */}
                    <div className={`flex items-center h-12 px-4 border-b border-gray-200 ${isCollapsed ? 'justify-center' : 'justify-end'}`}>
                        <button
                            onClick={() => setSidebarOpen(false)}
                            className="lg:hidden p-2 rounded-md text-gray-400 hover:text-gray-600"
                        >
                            <X size={20} />
                        </button>

                        {/* Desktop Collapse Toggle */}
                        <button
                            onClick={() => setIsCollapsed(!isCollapsed)}
                            className="hidden lg:block p-1 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100"
                        >
                            {isCollapsed ? <ChevronRight size={20} /> : <ChevronLeft size={20} />}
                        </button>
                    </div>

                    <nav className="mt-4 px-3 overflow-y-auto h-[calc(100%-3rem)]">
                        <div className="space-y-1">
                            {navigation.map((item) => {
                                const isActive = location.pathname === item.href;
                                if (item.children) {
                                    const isParentActive = location.pathname.startsWith(item.href) ||
                                        (item.children || []).some(child => location.pathname === child.href);
                                    const isOpen = openDropdown === item.name || isParentActive;

                                    return (
                                        <div key={item.name} className="space-y-1">
                                            <button
                                                className={`
                          w-full flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors duration-200
                          ${isParentActive
                                                        ? 'bg-primary-100 text-primary-700 border-r-2 border-primary-600'
                                                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                                                    }
                          ${isCollapsed ? 'justify-center' : 'justify-between'}
                        `}
                                                onClick={() => {
                                                    if (isCollapsed) {
                                                        setIsCollapsed(false);
                                                        setOpenDropdown(item.name);
                                                    } else {
                                                        setOpenDropdown(prev => prev === item.name ? null : item.name);
                                                    }
                                                }}
                                                title={isCollapsed ? item.name : ''}
                                            >
                                                <span className="flex items-center">
                                                    <item.icon
                                                        className={`
                              h-5 w-5 flex-shrink-0
                              ${!isCollapsed && 'mr-3'}
                              ${isParentActive ? 'text-primary-600' : 'text-gray-400 group-hover:text-gray-500'}
                            `}
                                                    />
                                                    {!isCollapsed && item.name}
                                                </span>
                                                {!isCollapsed && (
                                                    <svg className={`h-4 w-4 transition-transform ${isOpen ? 'transform rotate-90' : ''}`} viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                                        <path fillRule="evenodd" d="M6 6a1 1 0 011.707-.707l4 4a1 1 0 010 1.414l-4 4A1 1 0 016 13.586L9.586 10 6.707 7.121A1 1 0 016 6z" clipRule="evenodd" />
                                                    </svg>
                                                )}
                                            </button>
                                            {isOpen && !isCollapsed && (
                                                <div className="ml-8 space-y-1">
                                                    {item.children.map((child) => (
                                                        <NavLink
                                                            key={child.name}
                                                            to={child.href}
                                                            className={`
                                block px-3 py-1.5 text-sm rounded-md
                                ${location.pathname === child.href ? 'text-primary-700 bg-primary-50' : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'}
                              `}
                                                            onClick={() => setSidebarOpen(false)}
                                                        >
                                                            {child.name}
                                                        </NavLink>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    );
                                }
                                return (
                                    <NavLink
                                        key={item.name}
                                        to={item.href}
                                        className={`
                      group flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors duration-200
                      ${isActive
                                                ? 'bg-primary-100 text-primary-700 border-r-2 border-primary-600'
                                                : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                                            }
                      ${isCollapsed ? 'justify-center' : ''}
                    `}
                                        onClick={() => setSidebarOpen(false)}
                                        title={isCollapsed ? item.name : ''}
                                    >
                                        <item.icon
                                            className={`
                        h-5 w-5 flex-shrink-0
                        ${!isCollapsed && 'mr-3'}
                        ${isActive ? 'text-primary-600' : 'text-gray-400 group-hover:text-gray-500'}
                      `}
                                        />
                                        {!isCollapsed && item.name}
                                    </NavLink>
                                );
                            })}
                        </div>
                    </nav>
                </div>

                {/* Page content */}
                <main className="flex-1 overflow-y-auto bg-gray-50">
                    <div className="min-h-full p-4 sm:p-6 lg:p-8">
                        <Outlet />

                        {/* Footer */}
                        {!(location.pathname.startsWith('/invoice-generator') || location.pathname.startsWith('/invoices')) && (
                            <footer className="mt-8 border-t border-gray-200">
                                <div className="py-4 text-center text-xs text-gray-500">
                                    Powered by SoftNex
                                </div>
                            </footer>
                        )}
                    </div>
                </main>
            </div>
        </div>
    );
};

export default Layout;
